import { supabase } from '@/integrations/supabase/client';

interface ActivityLogOptions {
  userId: string;
  courseId: string;
  moduleId?: string;
  activityType: 'course_overview_access' | 'module_access' | 'module_completion' | 'quiz_completion' | 'practice_completion' | 'ai_tutor_access';
  metadata?: {
    module_title?: string;
    course_title?: string;
    progress_percentage?: number;
    time_spent?: number;
    [key: string]: any;
  };
}

export async function logActivity(options: ActivityLogOptions): Promise<void> {
  const { userId, courseId, moduleId, activityType, metadata = {} } = options;

  console.log('📝 Activity Logger Called:', {
    userId,
    courseId,
    moduleId,
    activityType,
    metadataKeys: Object.keys(metadata)
  });

  try {
    const insertData = {
      user_id: userId,
      course_id: courseId,
      module_id: moduleId || courseId,
      activity_type: activityType,
      metadata: metadata,
      created_at: new Date().toISOString()
    };

    console.log('🔄 Inserting activity:', insertData);

    const { data, error } = await supabase
      .from('module_activity_logs')
      .insert(insertData)
      .select();

    if (error) {
      console.error('❌ Activity insert error:', error);
      console.error('Error details:', JSON.stringify(error, null, 2));
      
      // Check for specific constraint violations
      if (error.message?.includes('activity_type_check') || error.code === '23514') {
        console.warn('⚠️ CONSTRAINT VIOLATION: Activity type not allowed in database');
        console.warn('💡 Run migration: supabase/migrations/20241112000000_add_new_activity_types.sql');
        console.warn(`📝 Attempted type: "${activityType}"`);
        console.warn('✅ Allowed types after migration: course_overview_access, module_access, session_start, etc.');
      }
    } else {
      console.log('✅ Activity logged successfully:', data);
    }
  } catch (err) {
    console.error('❌ Failed to log activity (exception):', err);
  }
}

export async function updateCourseAssignmentStatus(
  userId: string,
  courseId: string,
  status: 'active' | 'in_progress' | 'completed'
): Promise<void> {
  try {
    // First check if assignment exists
    const { data: existing } = await supabase
      .from('course_assignments')
      .select('id, status')
      .eq('user_id', userId)
      .eq('course_id', courseId)
      .single();

    if (existing) {
      // Only update if status is changing from active to in_progress or completed
      if (existing.status === 'active' && status !== 'active') {
        const { error } = await supabase
          .from('course_assignments')
          .update({
            status,
            last_activity_date: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .eq('id', existing.id);

        if (error) {
          console.error('Error updating course assignment status:', error);
        }
      }
    } else {
      // Create new assignment if doesn't exist
      const { error } = await supabase
        .from('course_assignments')
        .insert({
          user_id: userId,
          course_id: courseId,
          status,
          assignment_status: 'assigned',
          last_activity_date: new Date().toISOString()
        });

      if (error) {
        console.error('Error creating course assignment:', error);
      }
    }
  } catch (err) {
    console.error('Failed to update course assignment status:', err);
  }
}
